import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ArtifactOpener from './canvas/ArtifactOpener';
import {
  PAYMENT_UPDATED_PARAM,
  takeCheckoutResume,
  type CheckoutResume,
} from '../../utils/hostedCheckout';

export const ARTIFACT_RESUME_HOST = 'artifact';

interface ArtifactResumeState {
  artifactId: string;
}

/**
 * Resume descriptor for a spending approval hosted by a response artifact.
 * The artifact itself lives server-side, so remembering its id is enough to
 * bring it back after a same-tab card checkout.
 */
export function artifactCheckoutResume(
  artifactId: string,
): CheckoutResume<ArtifactResumeState> {
  return { host: ARTIFACT_RESUME_HOST, state: { artifactId } };
}

/**
 * Mounted once under the authenticated routes. When a hosted card checkout
 * returns to a page with an artifact resume record, re-opens that artifact so
 * the user lands back on the approval they were completing.
 */
const HostedCheckoutArtifactResume: React.FC = () => {
  const [, setSearchParams] = useSearchParams();
  const setSearchParamsRef = useRef(setSearchParams);
  setSearchParamsRef.current = setSearchParams;
  const [artifactId, setArtifactId] = useState<string | null>(null);

  useEffect(() => {
    const resume =
      takeCheckoutResume<ArtifactResumeState>(ARTIFACT_RESUME_HOST);
    if (!resume?.cardAdded) return;
    setArtifactId(resume.state.artifactId);
    setSearchParamsRef.current(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(PAYMENT_UPDATED_PARAM);
        return next;
      },
      { replace: true },
    );
  }, []);

  if (!artifactId) return null;
  return (
    <ArtifactOpener
      artifactId={artifactId}
      hideButton
      defaultOpen
      onClose={() => setArtifactId(null)}
    />
  );
};

export default HostedCheckoutArtifactResume;
